import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import GaugeValueDisplay from './GaugeValueDisplay';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getFormattedThresholdValue,
    getErrorMessageKey,
    getFormattedGaugeValue,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';
import { getValueStyles } from './gaugeValueDisplayUtils';

function getThreshold(thresholds) {
    if (_.isEmpty(thresholds)) {
        return {};
    }

    if (!thresholds[0].value) {
        return {
            thresholdErrorKey: 'missingThresholdValue',
        };
    }

    return { threshold: thresholds[0] };
}

/* eslint-disable complexity */
function getMetricSource(metricRequests, metrics) {
    if (metricRequests.length === 0 || metricRequests[0].metrics.length === 0) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource], true)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

export default function GaugeValueDisplayWrapper(props) {
    const { isTime, linkConfig, timeFormat, suffix = '' } = props.configuration;
    const { threshold, thresholdErrorKey } = getThreshold(props.thresholds);

    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props.metricRequests, props.metrics);

    if (errorKey || thresholdErrorKey) {
        const errorMessageKey = getErrorMessageKey(errorKey, thresholdErrorKey);
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const metricName = metricRequest.metrics[0];

    const formatter = isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const gaugeValue = metricSource.metricSeries[0]?.[metricName];

    const formattedGaugeValue = getFormattedGaugeValue(
        gaugeValue,
        formatter,
        suffix,
        timeFormat
    );

    const formattedThresholdValue = getFormattedThresholdValue(
        threshold,
        formatter,
        suffix,
        timeFormat
    );

    return (
        <GaugeValueDisplay
            gaugeValue={formattedGaugeValue}
            linkConfig={linkConfig}
            metricRequest={metricRequest}
            openLinksInNewTab={props.openLinksInNewTab}
            thresholdLabel={threshold?.label}
            thresholdValue={formattedThresholdValue}
            valueStyles={getValueStyles(gaugeValue, threshold)}
        />
    );
}
/* eslint-enable complexity */

GaugeValueDisplayWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    metricRequests: PropTypes.instanceOf(Array).isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
